import type { AuditLogFacetCount, AuditLogFacets } from "src/api/client-api/auditLog/auditLogSchemas";
import { foldText } from "src/components/FacetFilters/foldText";
import {
    AUDIT_LOG_DROPPABLE_FIELDS,
    AUDIT_LOG_QUERY_FIELDS,
    isAuditLogDroppableField,
    quote,
    type AuditLogQueryField,
} from "./auditLogQuery";

export type AuditLogSuggestion = {
    token: string;
    field: AuditLogQueryField | null;
    value: string | null;
    hint: string | null;
};

const STATIC_VALUES: Partial<Record<AuditLogQueryField, string[]>> = {
    action: ["create", "update", "delete"],
    actor_type: ["human", "service"],
    domain: ["role", "integration", "user"],
};

const FACET_VALUES: Partial<Record<AuditLogQueryField, keyof AuditLogFacets>> = {
    actor_id: "actors",
    entity_type: "entityTypes",
    scope: "scopes",
};

const isField = (value: string): value is AuditLogQueryField =>
    (AUDIT_LOG_QUERY_FIELDS as readonly string[]).includes(value);

const facetSuggestions = (
    field: AuditLogQueryField,
    facets: AuditLogFacets | undefined,
    prefix: string,
): AuditLogSuggestion[] => {
    const key = FACET_VALUES[field];

    if (key === undefined || facets === undefined) {
        return [];
    }

    const counts = facets[key] as AuditLogFacetCount[];

    return counts.map((facet) => ({
        token: `${prefix}${field}:${quote(facet.value)}`,
        field,
        value: facet.value,
        hint: facet.label ?? String(facet.count),
    }));
};

export const suggestAuditLogQueryTokens = (
    fragment: string,
    facets: AuditLogFacets | undefined,
): AuditLogSuggestion[] => {
    const trimmed = fragment.trim();
    const isDropped = trimmed.startsWith("-");
    const body = isDropped ? trimmed.slice(1) : trimmed;
    const prefix = isDropped ? "-" : "";
    const separator = body.indexOf(":");

    if (separator > 0) {
        const field = foldText(body.slice(0, separator));
        const typed = foldText(body.slice(separator + 1).replace(/^"|"$/g, ""));

        if (!isField(field) || (isDropped && !isAuditLogDroppableField(field))) {
            return [];
        }

        const candidates = [
            ...(STATIC_VALUES[field] ?? []).map((value) => ({
                token: `${prefix}${field}:${value}`,
                field,
                value,
                hint: null,
            })),
            ...facetSuggestions(field, facets, prefix),
        ];

        return candidates.filter(
            (candidate) =>
                typed === "" ||
                foldText(candidate.value ?? "").includes(typed) ||
                foldText(candidate.hint ?? "").includes(typed),
        );
    }

    const typed = foldText(body);
    const fields = isDropped ? AUDIT_LOG_DROPPABLE_FIELDS : AUDIT_LOG_QUERY_FIELDS;

    return fields
        .filter((field) => typed === "" || foldText(field).includes(typed))
        .map((field) => ({
            token: `${prefix}${field}:`,
            field,
            value: null,
            hint: null,
        }));
};
